// Données structurées Schema.org par route.
//
// Séparé volontairement du gestionnaire de balises Open Graph / Twitter :
// ce module ne construit que du JSON-LD, injecté par un composant dédié.
//
// Règle : n'ajouter un schéma que si le type est pertinent, que les faits
// requis sont disponibles et qu'ils correspondent au contenu visible.

import { BRAND } from "@/config/brand";
import { getSiteOrigin, SEO_IMAGE } from "@/config/seo";
import { faqs } from "@/mocks/public";
import { FORMULES_CATALOGUE } from "@/config/formules";

type JsonLd = Record<string, unknown>;

function absoluteImage(): string {
  return SEO_IMAGE;
}

function organizationNode(origin: string): JsonLd {
  return {
    "@type": "Organization",
    "@id": `${origin}/#organization`,
    name: BRAND.name,
    url: `${origin}/`,
    logo: absoluteImage(),
    image: absoluteImage(),
    email: BRAND.email,
    telephone: BRAND.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Avenue Kwame N'Krumah",
      addressLocality: "Ouagadougou",
      addressCountry: "BF",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: BRAND.email,
        telephone: BRAND.phone,
        availableLanguage: ["fr"],
      },
    ],
  };
}

function homeSchema(origin: string, prixMensuels?: number[]): JsonLd {
  const source = prixMensuels && prixMensuels.length > 0
    ? prixMensuels
    : FORMULES_CATALOGUE.map((formule) => formule.prixMensuel);
  const prices = source;
  const lowPrice = Math.min(...prices);
  const highPrice = Math.max(...prices);

  return {
    "@context": "https://schema.org",
    "@graph": [
      organizationNode(origin),
      {
        "@type": "WebSite",
        "@id": `${origin}/#website`,
        url: `${origin}/`,
        name: BRAND.name,
        description: BRAND.description,
        inLanguage: "fr",
        publisher: { "@id": `${origin}/#organization` },
      },
      {
        "@type": "SoftwareApplication",
        name: BRAND.name,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: `${origin}/`,
        description: BRAND.description,
        inLanguage: "fr",
        publisher: { "@id": `${origin}/#organization` },
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "XOF",
          lowPrice: String(lowPrice),
          highPrice: String(highPrice),
          offerCount: prixMensuels && prixMensuels.length > 0 ? prixMensuels.length : FORMULES_CATALOGUE.length,
        },
      },
    ],
  };
}

function faqSchema(origin: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${origin}/faq#faq`,
    url: `${origin}/faq`,
    inLanguage: "fr",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

function contactSchema(origin: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${origin}/contact#contact`,
    url: `${origin}/contact`,
    name: `Contact — ${BRAND.name}`,
    inLanguage: "fr",
    mainEntity: organizationNode(origin),
  };
}

/** Construit le JSON-LD pertinent pour un chemin, ou null si aucun. */
export function buildJsonLd(pathname: string, prixMensuels?: number[]): JsonLd | null {
  const path = (pathname || "/").split("?")[0].replace(/\/+$/, "") || "/";
  const origin = getSiteOrigin();

  if (path === "/") return homeSchema(origin, prixMensuels);
  if (path === "/faq") return faqSchema(origin);
  if (path === "/contact") return contactSchema(origin);
  return null;
}